import { useEffect, useRef } from 'react';

import { getRawSocket } from '~/utils/getRawSocket';

/**
 * Hook to prevent phone from sleeping and handle reconnection when returning from background.
 *
 * Uses:
 * 1. Wake Lock API to keep the screen on during gameplay
 * 2. Page Visibility API to detect when the player returns from background
 *    and force Socket.IO reconnection if needed
 */
export function useKeepAlive() {
    const wakeLockRef = useRef<WakeLockSentinel | null>(null);

    useEffect(() => {
        // --- Wake Lock API ---
        async function requestWakeLock() {
            if (!('wakeLock' in navigator)) return;
            try {
                wakeLockRef.current = await navigator.wakeLock.request('screen');
            } catch {
                // Wake lock request can fail (e.g. low battery, tab not visible)
            }
        }

        // --- Page Visibility API ---
        function handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                // Re-acquire wake lock (it gets released when tab is hidden)
                requestWakeLock();

                // Check if socket needs reconnection
                const wss = getRawSocket();
                if (wss && !wss.connected) {
                    console.log('[useKeepAlive] Tab visible again, socket disconnected — forcing reconnect');
                    wss.connect();
                }
            }
        }

        requestWakeLock();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (wakeLockRef.current) {
                wakeLockRef.current.release().catch(() => {});
                wakeLockRef.current = null;
            }
        };
    }, []);
}
